import React, {ReactNode, useCallback, useEffect, useState} from "react";
import {onIdTokenChanged, User} from "firebase/auth";
import {AuthContext} from "./AuthContext";
import {auth} from "../../../FirebaseConfig";
import {AuthUserData} from "../types/authentication";
import {fetchUserData} from "../utils/fetchUserData";


export const AuthManager: React.FC<{ children: ReactNode }> = ({ children }) => {

    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [userdata, setUserdata] = useState<AuthUserData | null>(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [userDataLoading, setUserDataLoading] = useState(false);

    const loadUserData = useCallback(async (currentToken: string | null) => {
        if (!currentToken) {
            setUserdata(null);
            return;
        }

        setUserDataLoading(true);

        try {
            const profile = await fetchUserData(currentToken);
            setUserdata(profile);
        } catch (error) {
            setUserdata(null);
        } finally {
            setUserDataLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!token) {
            setUserdata(null);
            return;
        }

        void loadUserData(token);
    }, [token, loadUserData]);

    useEffect(() => {
        // onIdTokenChanged fires on sign-in/sign-out as well as whenever Firebase
        // refreshes the ID token, keeping the context token in sync (onAuthStateChanged
        // only fires once per sign-in, which let the token go stale after inactivity).
        return onIdTokenChanged(auth, async (user) => {
            if (user) {
                setFirebaseUser(user);
                const idToken = await user.getIdToken();
                setToken(idToken);
            } else {
                setFirebaseUser(null);
                setToken(null);
            }
            setAuthLoading(false);
        });
    }, []);

    const logout = async () => {
        await auth.signOut();
        setFirebaseUser(null);
        setToken(null);
        setUserdata(null);
    };

    const value = {
        firebaseUser,
        token,
        loading: authLoading || userDataLoading,
        isAuthenticated: !!firebaseUser,
        isAdmin: userdata?.type === "admin",
        setFirebaseUser: (user: User, token: string) => {
            setFirebaseUser(user);
            setToken(token);
        },
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};